import { useState, useEffect } from 'react';
import { CURRENCIES, fmtAmt } from '../../shared/src/currencies';
import { GameSDK } from '../../shared/src/game-sdk';
import { ProvablyFair } from '../../shared/src/provably-fair';
import PixiGame from './components/PixiGame';
import TowerGame from './components/TowerGame';
import RacingGame from './components/RacingGame';
import { 
  Wallet, 
  Gamepad2, 
  Code, 
  ArrowDownCircle, 
  ArrowUpCircle, 
  Send,
  ShieldCheck
} from 'lucide-react';

const BACKEND_HOST = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'localhost:8787'
  : '9f-game-platform-api.zeyelvis.workers.dev';

const API_BASE = `${window.location.protocol === 'https:' ? 'https' : 'http'}://${BACKEND_HOST}`;
const WS_BASE = `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${BACKEND_HOST}`;

export default function App() {
  const [activeCurrency, setActiveCurrency] = useState('usdt');
  const [balances, setBalances] = useState<Record<string, number>>({
    usdt: 120.0,
    trx: 850.0,
    ton: 15.5,
    eth: 0.045,
  });
  const [lastBetAmount, setLastBetAmount] = useState(1.0);
  const [connectedWallet, setConnectedWallet] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'lobby' | 'api' | 'finance'>('lobby');
  const [activeGame, setActiveGame] = useState<'crash' | 'tower' | 'racing'>('crash');
  
  // 资金账目与工作流拦截状态
  const [userTransactions, setUserTransactions] = useState<any[]>([]);
  const [adminWithdrawals, setAdminWithdrawals] = useState<any[]>([]);
  
  // 充值提现弹窗状态
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [depositAddress, setDepositAddress] = useState('');
  const [withdrawAddress, setWithdrawAddress] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');

  // 公平性校验弹窗状态
  const [showFairnessModal, setShowFairnessModal] = useState(false);
  const [verifyServerSeed, setVerifyServerSeed] = useState('plain_secret_room_seed_2026');
  const [verifyClientSeed, setVerifyClientSeed] = useState('shared_platform_client_seed');
  const [verifyNonce, setVerifyNonce] = useState(1);
  const [verifyResult, setVerifyResult] = useState<string | null>(null);

  // 商户 API 面板状态
  const providerId = 'provider_9f_slots';
  const apiKey = 'apikey_mock_key_2026';
  const apiSecret = 'secret_mock_secret_2026';
  const [apiConsoleLogs, setApiConsoleLogs] = useState<string[]>([]);

  // 历史开奖与一键校验状态
  const [history, setHistory] = useState<any[]>([]);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [isLoadingGame, setIsLoadingGame] = useState(false);

  // 大厅聊天室与大奖喜报状态
  const [lobbyMessages, setLobbyMessages] = useState<any[]>([]);
  const [bigWinAlert, setBigWinAlert] = useState<any>(null);
  const [chatInput, setChatInput] = useState('');
  const [wsConnected, setWsConnected] = useState(false);
  const [userId, setUserId] = useState(() => localStorage.getItem('9f_user_id') || 'user_web_' + Math.random().toString(36).substring(2, 8));
  const [username, setUsername] = useState(() => 'Player_' + userId.substring(userId.startsWith('user_web_') ? 9 : 0));
  const [socket, setSocket] = useState<WebSocket | null>(null);

  useEffect(() => {
    const savedUserId = localStorage.getItem('9f_user_id');
    const savedAddress = localStorage.getItem('9f_connected_address') || '';
    if (connectedWallet && savedUserId) {
      setUserId(savedUserId);
      const name = `Web3_${savedAddress.substring(0, 6)}...${savedAddress.substring(savedAddress.length - 4)}`;
      setUsername(name);
    } else {
      const tempId = 'user_web_' + Math.random().toString(36).substring(2, 8);
      setUserId(tempId);
      setUsername('Player_' + tempId.substring(9));
    }
  }, [connectedWallet]);

  useEffect(() => {
    // 建立与大厅的 WebSocket 连接
    const wsUrl = `${WS_BASE}/api/lobby/ws?userId=${userId}&username=${username}`;
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      setWsConnected(true);
      console.log('Lobby WebSocket connected');
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'CHAT_MSG' || data.type === 'SYSTEM_MSG') {
          setLobbyMessages((prev) => [...prev.slice(-49), data]); // 保留最近 50 条
        } else if (data.type === 'GLOBAL_WIN_ALERT') {
          setBigWinAlert(data);
          // 4.5秒后自动隐退
          setTimeout(() => {
            setBigWinAlert(null);
          }, 4500);
        }
      } catch (err) {
        console.error('Failed to parse lobby message:', err);
      }
    };

    ws.onclose = () => {
      setWsConnected(false);
      console.log('Lobby WebSocket disconnected');
    };

    ws.onerror = (err) => {
      console.error('Lobby WebSocket error:', err);
    };

    setSocket(ws);

    return () => {
      ws.close();
    };
  }, [userId, username]);

  const handleSendChat = () => {
    if (!chatInput.trim()) return;
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      showToast('大厅聊天室连接已断开，请稍后再试', 'error');
      return;
    }
    socket.send(JSON.stringify({
      type: 'SEND_CHAT',
      text: chatInput
    }));
    setChatInput('');
  };

  // Web3 真实鉴权余额获取
  const fetchBalances = async (token = localStorage.getItem('9f_jwt_token')) => {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE}/api/user/balance`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await res.json();
      if (data.success && data.balances) {
        setBalances(data.balances);
        logApiEvent(`余额数据拉取成功: ${JSON.stringify(data.balances)}`);
      }
    } catch (err: any) {
      console.error('Failed to fetch balance:', err);
    }
  };

  // 账目流水审计获取
  const fetchTransactions = async (token = localStorage.getItem('9f_jwt_token')) => {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE}/api/user/transactions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success && data.transactions) {
        setUserTransactions(data.transactions);
      }
    } catch (err) {
      console.error('Failed to fetch transactions:', err);
    }
  };

  // 管理端提现风控列表拉取
  const fetchAdminWithdrawals = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/withdrawals`);
      const data = await res.json();
      if (data.success && data.withdrawals) {
        setAdminWithdrawals(data.withdrawals);
      }
    } catch (err) {
      console.error('Failed to fetch withdrawals list:', err);
    }
  };

  // 模拟管理员审核：批准出款并推进工作流
  const handleApproveWithdraw = async (withdrawalId: number) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/withdrawals/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ withdrawalId })
      });
      const data = await res.json();
      if (data.success) {
        showToast('🎉 提现审核通过！已向工作流发出恢复出款信号。', 'success');
        setTimeout(() => {
          fetchAdminWithdrawals();
          fetchBalances();
          fetchTransactions();
        }, 1500);
      } else {
        showToast('审核通过操作失败: ' + data.error, 'error');
      }
    } catch (err: any) {
      showToast('审核通信发生错误: ' + err.message, 'error');
    }
  };

  // 模拟管理员审核：拒绝提现并原路退款回 DO
  const handleRejectWithdraw = async (withdrawalId: number) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/withdrawals/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ withdrawalId })
      });
      const data = await res.json();
      if (data.success) {
        showToast('❌ 提现请求已被退回！资金已安全返还用户钱包。', 'info');
        fetchAdminWithdrawals();
        fetchBalances();
        fetchTransactions();
      } else {
        showToast('审核拒绝操作失败: ' + data.error, 'error');
      }
    } catch (err: any) {
      showToast('审核通信发生错误: ' + err.message, 'error');
    }
  };

  // 钱包自动登录恢复
  useEffect(() => {
    const savedToken = localStorage.getItem('9f_jwt_token');
    const savedWallet = localStorage.getItem('9f_connected_wallet_type');
    if (savedToken && savedWallet) {
      setConnectedWallet(savedWallet);
      fetchBalances(savedToken);
      fetchTransactions(savedToken);
    }
  }, []);

  // Web3 签名登录对接
  const connectWallet = async (walletType: string) => {
    try {
      const chain = walletType === 'TON Connect' ? 'ton' : 'evm';
      const address = chain === 'ton' 
        ? 'EQD_' + Math.random().toString(36).substring(2, 12) + '_ton_user_wallet'
        : '0x' + Math.random().toString(36).substring(2, 10) + Math.random().toString(36).substring(2, 10) + 'EVMAddress';

      const proof = chain === 'ton' ? {
        timestamp: Math.floor(Date.now() / 1000),
        domain: 'localhost',
        payload: 'session-payload-token-2026',
        signature: 'mock_ton_signature_proof_2026'
      } : undefined;

      const signature = chain === 'evm' ? 'mock_evm_hex_signature_2026' : undefined;
      const message = chain === 'evm' ? 'Sign-in to 9F Platform: 2026' : undefined;

      const res = await fetch(`${API_BASE}/api/auth/wallet-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chain, address, signature, message, proof })
      });

      const data = await res.json();
      if (data.success && data.token) {
        localStorage.setItem('9f_jwt_token', data.token);
        localStorage.setItem('9f_connected_wallet_type', walletType);
        localStorage.setItem('9f_connected_address', address);
        localStorage.setItem('9f_user_id', String(data.user.id));
        
        setConnectedWallet(walletType);
        showToast(`🎉 Web3 钱包登录成功！\n连接方式: ${walletType}\n地址: ${address.substring(0, 6)}...${address.substring(address.length - 4)}`, 'success');
        
        fetchBalances(data.token);
        fetchTransactions(data.token);
      } else {
        showToast('钱包登录校验失败: ' + (data.error || '未知错误'), 'error');
      }
    } catch (err: any) {
      showToast('钱包登录发生错误: ' + err.message, 'error');
    }
  };

  const disconnectWallet = () => {
    localStorage.removeItem('9f_jwt_token');
    localStorage.removeItem('9f_connected_wallet_type');
    localStorage.removeItem('9f_connected_address');
    localStorage.removeItem('9f_user_id');
    setConnectedWallet(null);
    setBalances({
      usdt: 0,
      trx: 0,
      ton: 0,
      eth: 0,
    });
    showToast('已断开钱包连接', 'info');
  };

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    setToast({ message, type });
    setTimeout(() => {
      setToast(null);
    }, 4000);
  };

  const fetchHistory = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/game/history?gameId=${activeGame}`);
      const data = await res.json();
      if (data.success) {
        setHistory(data.history || []);
      }
    } catch (err) {
      console.error('Failed to fetch history:', err);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, [activeGame]);

  useEffect(() => {
    setIsLoadingGame(true);
    const timer = setTimeout(() => {
      setIsLoadingGame(false);
    }, 500);
    return () => clearTimeout(timer);
  }, [activeGame]);

  // 1. 初始化 Game SDK
  useEffect(() => {
    const sdk = new GameSDK();
    const cleanListener = sdk.onMessage((req) => {
      logApiEvent(`[SDK Request Received] 类型: ${req.type}`);
      if ((req as any).type === 'PLACE_BET') {
        const { amount } = req as any;
        handleBetPlaced(amount).then(success => {
          window.postMessage({ type: 'BET_RESULT', success, balance: balances[activeCurrency] }, '*');
        });
      } else if ((req as any).type === 'CLAIM_WIN') {
        const { multiplier } = req as any;
        handleWinClaimed(multiplier);
      }
    });

    return () => cleanListener();
  }, [balances, activeCurrency]);

  const logApiEvent = (msg: string) => {
    setApiConsoleLogs(prev => [`[${new Date().toLocaleTimeString()}] ${msg}`, ...prev.slice(0, 10)]);
  };

  const handleBetPlaced = async (amount: number): Promise<boolean> => {
    const token = localStorage.getItem('9f_jwt_token');
    if (!token) {
      showToast('请先连接 Telegram 钱包！', 'error');
      return false;
    }

    if (balances[activeCurrency] < amount) {
      showToast('余额不足，下注失败！', 'error');
      return false;
    }

    try {
      const res = await fetch(`${API_BASE}/api/user/game/bet`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          currency: activeCurrency,
          amount,
          gameId: activeGame
        })
      });

      const data = await res.json();
      if (data.success) {
        setBalances(prev => ({
          ...prev,
          [activeCurrency]: data.balance
        }));
        setLastBetAmount(amount);
        logApiEvent(`下注扣款成功: -${amount} ${activeCurrency.toUpperCase()}`);
        fetchTransactions(token);
        return true;
      } else {
        showToast('扣款失败: ' + (data.error || '未知错误'), 'error');
        return false;
      }
    } catch (err: any) {
      showToast('下注通信发生错误: ' + err.message, 'error');
      return false;
    }
  };

  const handleWinClaimed = async (multiplier: number) => {
    if (multiplier <= 0) return;
    const token = localStorage.getItem('9f_jwt_token');
    if (!token) {
      showToast('请先连接 Telegram 钱包！', 'error');
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/api/user/game/win`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          currency: activeCurrency,
          multiplier,
          gameId: activeGame,
          roundId: `round_${activeGame}_${Date.now()}`
        })
      });

      const data = await res.json();
      if (data.success) {
        setBalances(prev => ({
          ...prev,
          [activeCurrency]: data.balance
        }));
        showToast(`🎉 结算成功！\n投注: ${lastBetAmount} ${activeCurrency.toUpperCase()} | 赢取: ${data.winAmount} ${activeCurrency.toUpperCase()}`, 'success');
        logApiEvent(`游戏结算入账: +${data.winAmount} ${activeCurrency.toUpperCase()} (倍数: ${multiplier.toFixed(2)}x)`);
        fetchTransactions(token);
      } else {
        showToast('结算上分失败: ' + (data.error || '未知错误'), 'error');
      }
    } catch (err: any) {
      showToast('结算通信发生错误: ' + err.message, 'error');
    }
  };

  const handleDepositClick = async () => {
    const token = localStorage.getItem('9f_jwt_token');
    if (!token) {
      showToast('请先连接钱包！', 'error');
      return;
    }
    try {
      const chain = CURRENCIES[activeCurrency].chain;
      const res = await fetch(`${API_BASE}/api/user/wallet/deposit-address?chain=${chain}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await res.json();
      if (data.success) {
        setDepositAddress(data.address);
        setShowDepositModal(true);
      } else {
        showToast('获取充值地址失败: ' + (data.error || '未知错误'), 'error');
      }
    } catch (err: any) {
      showToast('获取充值地址发生错误: ' + err.message, 'error');
    }
  };

  const handleDepositSubmit = async () => {
    const token = localStorage.getItem('9f_jwt_token');
    const dbUserId = localStorage.getItem('9f_user_id');
    if (!token || !dbUserId) {
      showToast('请先连接钱包！', 'error');
      return;
    }
    
    // 根据当前选择的币种，自动充值一个合理的沙盒测试金额
    const amountMap: Record<string, number> = {
      usdt: 100.0,
      trx: 500.0,
      ton: 10.0,
      eth: 0.05
    };
    const amt = amountMap[activeCurrency] || 10.0;
    const chain = CURRENCIES[activeCurrency].chain;
    const txHash = `0x_mock_deposit_tx_${Math.random().toString(36).substring(2, 10)}${Math.random().toString(36).substring(2, 10)}`;

    try {
      const res = await fetch(`${API_BASE}/api/finance/deposit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          userId: parseInt(dbUserId, 10),
          chain,
          currency: activeCurrency,
          amount: amt,
          txHash
        })
      });

      const data = await res.json();
      if (data.success) {
        showToast(`🎉 充值请求提交成功！\n已自动入账: +${amt} ${activeCurrency.toUpperCase()}`, 'success');
        setShowDepositModal(false);
        // 延时 800ms 刷新余额与流水，等待后端 Workflows 异步核销上分
        setTimeout(() => {
          fetchBalances(token);
          fetchTransactions(token);
        }, 800);
      } else {
        showToast('充值入账失败: ' + (data.error || '已处理的重复哈希'), 'error');
        setShowDepositModal(false);
      }
    } catch (err: any) {
      showToast('充值提交发生错误: ' + err.message, 'error');
      setShowDepositModal(false);
    }
  };

  const handleWithdrawSubmit = async () => {
    const token = localStorage.getItem('9f_jwt_token');
    if (!token) {
      showToast('请先连接钱包！', 'error');
      return;
    }

    const amt = parseFloat(withdrawAmount);
    if (!withdrawAddress || isNaN(amt) || amt <= 0) {
      showToast('请输入合法的提现地址和金额', 'error');
      return;
    }
    if (balances[activeCurrency] < amt) {
      showToast('余额不足', 'error');
      return;
    }

    try {
      const chain = CURRENCIES[activeCurrency].chain;
      const res = await fetch('http://localhost:8787/api/user/wallet/withdraw', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          currency: activeCurrency,
          amount: amt,
          address: withdrawAddress,
          chain
        })
      });

      const data = await res.json();
      if (data.success) {
        setShowWithdrawModal(false);
        setWithdrawAmount('');
        
        if (data.status === 'pending_approval') {
          showToast(`⚠️ 大额提现已安全挂起！\n估值超过限额，提现进入风控审批队列。`, 'info');
        } else {
          showToast(`提现已提交！金额: ${amt} ${activeCurrency.toUpperCase()}，热钱包自动出款中...`, 'success');
          setTimeout(() => {
            showToast(`🎉 自动提现成功！已广播出款至地址：\n${withdrawAddress.substring(0, 8)}...${withdrawAddress.substring(withdrawAddress.length - 8)}`, 'success');
          }, 2000);
        }
        
        fetchBalances(token);
        fetchTransactions(token);
        logApiEvent(`提现扣款成功: -${amt} ${activeCurrency.toUpperCase()}`);
      } else {
        showToast('提现失败: ' + (data.error || '未知错误'), 'error');
      }
    } catch (err: any) {
      showToast('提现提交发生错误: ' + err.message, 'error');
    }
  };

  const handleVerifyFairness = async (serverSeed = verifyServerSeed, clientSeed = verifyClientSeed, nonce = verifyNonce) => {
    const hashedServer = await ProvablyFair.sha256(serverSeed);
    const crashMultiplier = await ProvablyFair.calculateCrashOutcome(serverSeed, clientSeed, nonce);
    const slotsReels = await ProvablyFair.calculateSlotsOutcome(serverSeed, clientSeed, nonce);

    setVerifyResult(`
      服务端哈希(Server Hash): ${hashedServer}
      Crash 计算结果: ${crashMultiplier.toFixed(2)}x
      Slots 计算结果: [${slotsReels.join(', ')}]
    `);
  };

  return (
    <div className="app-container">
      {bigWinAlert && (
        <div className="win-alert-container">
          <div className="win-alert-card">
            🏆 恭喜 {bigWinAlert.username} 喜获 {bigWinAlert.multiplier.toFixed(2)}x 大奖！(赢取: {bigWinAlert.profit} {bigWinAlert.currency.toUpperCase()})
          </div>
        </div>
      )}
      {/* 顶部标题栏 */}
      <header className="header-card">
        <div className="brand">
          <span className="brand-icon">🛸</span>
          <span className="brand-title">9F PLATFORM</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <button 
            onClick={() => setShowFairnessModal(true)}
            style={{
              background: 'rgba(0, 255, 255, 0.1)', border: '1px solid var(--color-primary)',
              borderRadius: '20px', padding: '4px 10px', color: 'var(--color-primary)',
              fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer'
            }}
          >
            <ShieldCheck size={12} /> 公平校验
          </button>
          <div className="pulsing-dot"></div>
        </div>
      </header>

      {/* 1. 多链钱包资产管理区域 */}
      <section className="glass-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
          <h4 style={{ fontSize: '14px', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Wallet size={16} /> Web3 多链资产管理
          </h4>
          <span style={{ fontSize: '11px', background: 'rgba(0,255,255,0.1)', color: 'var(--color-primary)', padding: '2px 8px', borderRadius: '20px' }}>
            {connectedWallet ? `钱包已连接` : '未连接钱包'}
          </span>
        </div>

        {!connectedWallet ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <button className="btn-neon" style={{ padding: '12px', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }} onClick={() => connectWallet('TON Connect')}>
              💎 连接 Telegram 钱包 (TON Connect)
            </button>
          </div>
        ) : (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px', color: 'var(--text-muted)', marginBottom: '8px' }}>
              <span>已连接: {connectedWallet}</span>
              <button style={{ background: 'none', border: 'none', color: 'var(--color-accent)', cursor: 'pointer' }} onClick={disconnectWallet}>断开连接</button>
            </div>
            
            {/* 币种选择器 */}
            <div className="wallet-grid">
              {Object.values(CURRENCIES).map(cur => (
                <div 
                  key={cur.id} 
                  className="balance-item"
                  style={{ 
                    borderColor: activeCurrency === cur.id ? 'var(--color-primary)' : 'var(--border-glass)',
                    background: activeCurrency === cur.id ? 'rgba(0,255,255,0.06)' : 'rgba(255,255,255,0.02)',
                    cursor: 'pointer'
                  }}
                  onClick={() => setActiveCurrency(cur.id)}
                >
                  <span style={{ fontSize: '20px' }}>{cur.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '9px', color: 'var(--text-muted)' }}>{cur.name} ({cur.chain.toUpperCase()})</div>
                    <div style={{ fontSize: '13px', fontWeight: 'bold' }}>{fmtAmt(balances[cur.id], cur.id)}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* 充提快捷按钮 */}
            <div style={{ display: 'flex', gap: '10px', marginTop: '14px' }}>
              <button className="btn-neon" onClick={handleDepositClick} style={{ flex: 1, padding: '10px', fontSize: '13px', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-glass-active)', boxShadow: 'none' }}>
                <ArrowDownCircle size={16} /> 充 值
              </button>
              <button className="btn-neon" onClick={() => setShowWithdrawModal(true)} style={{ flex: 1, padding: '10px', fontSize: '13px', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-glass-active)', boxShadow: 'none' }}>
                <ArrowUpCircle size={16} /> 提 现
              </button>
            </div>
          </div>
        )}
      </section>

      {/* 2. 标签导航 */}
      <div style={{ display: 'flex', padding: '0 16px', gap: '8px' }}>
        <button 
          className="btn-neon" 
          style={{
            flex: 1, padding: '8px', fontSize: '12px',
            background: activeTab === 'lobby' ? 'var(--bg-surface-hover)' : 'transparent',
            borderColor: activeTab === 'lobby' ? 'var(--color-primary)' : 'transparent',
            boxShadow: 'none'
          }}
          onClick={() => setActiveTab('lobby')}
        >
          <Gamepad2 size={14} /> 游戏大厅
        </button>
        <button 
          className="btn-neon" 
          style={{
            flex: 1, padding: '8px', fontSize: '12px',
            background: activeTab === 'api' ? 'var(--bg-surface-hover)' : 'transparent',
            borderColor: activeTab === 'api' ? 'var(--color-primary)' : 'transparent',
            boxShadow: 'none'
          }}
          onClick={() => setActiveTab('api')}
        >
          <Code size={14} /> 对外 API
        </button>
        <button 
          className="btn-neon" 
          style={{
            flex: 1, padding: '8px', fontSize: '12px',
            background: activeTab === 'finance' ? 'var(--bg-surface-hover)' : 'transparent',
            borderColor: activeTab === 'finance' ? 'var(--color-primary)' : 'transparent',
            boxShadow: 'none'
          }}
          onClick={() => {
            setActiveTab('finance');
            fetchTransactions();
            fetchAdminWithdrawals();
          }}
        >
          <ShieldCheck size={14} /> 资金安全
        </button>
      </div>

      {/* 3. 游戏大厅主体 */}
      {activeTab === 'lobby' && (
        <>
          {/* 三端引擎子菜单 */}
          <div style={{ display: 'flex', padding: '12px 16px 0', gap: '8px', overflowX: 'auto' }}>
            <button 
              onClick={() => setActiveGame('crash')}
              style={{
                padding: '6px 12px', borderRadius: '20px', border: 'none',
                background: activeGame === 'crash' ? 'var(--color-primary)' : 'rgba(255,255,255,0.05)',
                color: activeGame === 'crash' ? '#000' : 'var(--text-main)',
                fontSize: '11px', fontWeight: 'bold', cursor: 'pointer'
              }}
            >
              Crash 2D (PixiJS)
            </button>
            <button 
              onClick={() => setActiveGame('tower')}
              style={{
                padding: '6px 12px', borderRadius: '20px', border: 'none',
                background: activeGame === 'tower' ? 'var(--color-secondary)' : 'rgba(255,255,255,0.05)',
                color: activeGame === 'tower' ? '#000' : 'var(--text-main)',
                fontSize: '11px', fontWeight: 'bold', cursor: 'pointer'
              }}
            >
              Tower 2.5D (Three.js)
            </button>
            <button 
              onClick={() => setActiveGame('racing')}
              style={{
                padding: '6px 12px', borderRadius: '20px', border: 'none',
                background: activeGame === 'racing' ? 'var(--color-accent)' : 'rgba(255,255,255,0.05)',
                color: activeGame === 'racing' ? '#000' : 'var(--text-main)',
                fontSize: '11px', fontWeight: 'bold', cursor: 'pointer'
              }}
            >
              Racing 3D (Babylon.js)
            </button>
          </div>

          {/* 历史倍率走势气泡栏 */}
          {history.length > 0 && (
            <div style={{ display: 'flex', gap: '8px', padding: '10px 16px 0', overflowX: 'auto', whiteSpace: 'nowrap' }}>
              {history.map((round: any) => (
                <span 
                  key={round.round_id} 
                  onClick={() => {
                    setVerifyServerSeed(round.server_seed);
                    setVerifyClientSeed(round.client_seed);
                    setVerifyNonce(round.nonce);
                    setShowFairnessModal(true);
                    handleVerifyFairness(round.server_seed, round.client_seed, round.nonce);
                  }}
                  style={{
                    padding: '4px 10px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    background: round.outcome >= 2.0 ? 'rgba(0, 255, 128, 0.15)' : 'rgba(255, 60, 60, 0.15)',
                    border: `1px solid ${round.outcome >= 2.0 ? 'rgba(0, 255, 128, 0.3)' : 'rgba(255, 60, 60, 0.3)'}`,
                    color: round.outcome >= 2.0 ? 'hsl(140, 90%, 60%)' : 'hsl(0, 90%, 65%)',
                    transition: 'var(--transition-smooth)'
                  }}
                >
                  {round.outcome.toFixed(2)}x
                </span>
              ))}
            </div>
          )}

          {isLoadingGame ? (
            <div className="glass-card" style={{ height: '340px', display: 'flex', flexDirection: 'column', gap: '16px', justifyContent: 'center', padding: '24px' }}>
              <div className="skeleton-loader" style={{ height: '24px', width: '60%' }}></div>
              <div className="skeleton-loader" style={{ flex: 1 }}></div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <div className="skeleton-loader" style={{ height: '40px', flex: 1 }}></div>
                <div className="skeleton-loader" style={{ height: '40px', flex: 1 }}></div>
              </div>
            </div>
          ) : (
            <>
              {activeGame === 'crash' && (
                <PixiGame activeCurrency={activeCurrency} onBetPlaced={handleBetPlaced} onWinClaimed={handleWinClaimed} balance={balances[activeCurrency]} onGameCrashed={fetchHistory} />
              )}
              {activeGame === 'tower' && (
                <TowerGame activeCurrency={activeCurrency} onBetPlaced={handleBetPlaced} onWinClaimed={handleWinClaimed} balance={balances[activeCurrency]} />
              )}
              {activeGame === 'racing' && (
                <RacingGame activeCurrency={activeCurrency} onBetPlaced={handleBetPlaced} onWinClaimed={handleWinClaimed} balance={balances[activeCurrency]} />
              )}
            </>
          )}

          {/* 大厅多人联机聊天框 */}
          <div className="glass-card" style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-glass)', paddingBottom: '8px' }}>
              <div style={{ fontSize: '13px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '6px' }}>
                💬 大厅实时聊天室
                <span style={{ 
                  display: 'inline-block', 
                  width: '6px', 
                  height: '6px', 
                  borderRadius: '50%', 
                  background: wsConnected ? '#00ff80' : '#ff3c3c',
                  boxShadow: wsConnected ? '0 0 8px #00ff80' : '0 0 8px #ff3c3c'
                }}></span>
              </div>
              <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                {wsConnected ? '在线联机' : '未连接'}
              </span>
            </div>

            {/* 消息滚动区 */}
            <div style={{ 
              height: '150px', 
              overflowY: 'auto', 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '6px', 
              padding: '8px',
              background: 'rgba(0,0,0,0.2)',
              borderRadius: '6px'
            }}>
              {lobbyMessages.map((msg, index) => {
                if (msg.type === 'SYSTEM_MSG') {
                  return (
                    <div key={index} style={{ fontSize: '11px', color: 'var(--color-primary)', fontStyle: 'italic', textAlign: 'center', opacity: 0.8 }}>
                      📢 {msg.text}
                    </div>
                  );
                }
                return (
                  <div key={index} style={{ fontSize: '11px', lineHeight: '1.4' }}>
                    <span style={{ 
                      color: msg.userId === userId ? 'var(--color-accent)' : 'var(--color-secondary)', 
                      fontWeight: 'bold',
                      marginRight: '6px'
                    }}>
                      {msg.username}
                    </span>
                    <span style={{ color: 'var(--text-muted)', fontSize: '9px', marginRight: '6px' }}>
                      {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                    </span>
                    <span style={{ color: 'var(--text-main)' }}>{msg.text}</span>
                  </div>
                );
              })}
              {lobbyMessages.length === 0 && (
                <div style={{ margin: 'auto', fontSize: '11px', color: 'var(--text-muted)' }}>
                  暂无聊天内容，发送消息打破寂静吧！
                </div>
              )}
            </div>

            {/* 输入发送框 */}
            <div style={{ display: 'flex', gap: '8px' }}>
              <input 
                type="text" 
                value={chatInput} 
                onChange={(e) => setChatInput(e.target.value)} 
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSendChat();
                }}
                placeholder="说点什么吧..." 
                style={{ 
                  flex: 1, 
                  background: 'rgba(255,255,255,0.05)', 
                  border: '1px solid var(--border-glass)', 
                  borderRadius: '20px', 
                  padding: '6px 14px', 
                  color: 'var(--text-main)', 
                  fontSize: '12px',
                  outline: 'none'
                }}
              />
              <button 
                onClick={handleSendChat}
                className="btn-neon"
                style={{ 
                  padding: '6px 14px', 
                  borderRadius: '20px', 
                  fontSize: '12px',
                  boxShadow: 'none'
                }}
              >
                <Send size={14} />
              </button>
            </div>
          </div>
        </>
      )}

      {/* 4. 对外 API 开放平台大厅 */}
      {activeTab === 'api' && (
        <section className="glass-card" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <h3 style={{ fontSize: '18px', color: 'var(--color-primary)' }}>商户对接调试面板</h3>
          <p style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
            通过 HMAC-SHA256 签名方式将第三方游戏服务器（如 Slots 游戏）安全接入我们的结算网关。
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div>
              <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Provider ID</label>
              <input type="text" value={providerId} readOnly style={{ width: '100%', background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-glass)', borderRadius: '6px', padding: '8px', color: 'var(--text-main)' }} />
            </div>
            <div>
              <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>API Key</label>
              <input type="text" value={apiKey} readOnly style={{ width: '100%', background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-glass)', borderRadius: '6px', padding: '8px', color: 'var(--text-main)' }} />
            </div>
            <div>
              <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>HMAC Key Secret</label>
              <input type="text" value={apiSecret} readOnly style={{ width: '100%', background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-glass)', borderRadius: '6px', padding: '8px', color: 'var(--text-main)' }} />
            </div>
          </div>

          <div style={{ background: '#000', borderRadius: '8px', padding: '12px', height: '120px', overflowY: 'auto' }}>
            <div style={{ fontSize: '10px', color: 'var(--color-primary)', borderBottom: '1px solid #222', paddingBottom: '4px', marginBottom: '6px' }}>
              SDK 通信与 API 日志监听控制台
            </div>
            {apiConsoleLogs.map((log, idx) => (
              <div key={idx} style={{ fontSize: '10px', color: '#8af', fontFamily: 'monospace', margin: '2px 0' }}>{log}</div>
            ))}
            {apiConsoleLogs.length === 0 && (
              <div style={{ fontSize: '10px', color: '#444', textAlign: 'center', marginTop: '20px' }}>暂无日志事件...</div>
            )}
          </div>
        </section>
      )}

      {/* 4.5 资金安全与工作流风控监控面板 */}
      {activeTab === 'finance' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* 风审控制台 */}
          <section className="glass-card" style={{ margin: '0 16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <h3 style={{ fontSize: '15px', color: 'var(--color-accent)', display: 'flex', alignItems: 'center', gap: '6px' }}>
              🛡️ 提现风控人工审批台 (测试视角)
            </h3>
            <p style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
              估值超过 100 USD 的大额提现会被自动拦截并挂起在 Workflows 审核步骤。在此处一键审批推进工作流：
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '180px', overflowY: 'auto' }}>
              {adminWithdrawals.filter(w => w.status === 'pending_approval').map((w: any) => (
                <div key={w.id} style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border-glass)', borderRadius: '10px', padding: '10px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px' }}>
                    <span style={{ color: 'var(--text-main)', fontWeight: 'bold' }}>流水 #{w.id}</span>
                    <span style={{ color: 'var(--color-accent)' }}>待风审</span>
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                    用户: {w.username} | 提现: <strong style={{ color: '#fff' }}>{w.amount} {w.currency.toUpperCase()}</strong>
                  </div>
                  <div style={{ fontSize: '9px', fontFamily: 'monospace', wordBreak: 'break-all', color: 'var(--text-muted)' }}>
                    目标地址: {w.to_address}
                  </div>
                  {/* 审核按钮 */}
                  <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                    <button 
                      onClick={() => handleApproveWithdraw(w.id)}
                      style={{ 
                        flex: 1, padding: '6px', fontSize: '11px', borderRadius: '6px', border: '1px solid rgba(0, 255, 128, 0.4)', 
                        background: 'rgba(0, 255, 128, 0.1)', color: 'hsl(140, 90%, 60%)', cursor: 'pointer', fontWeight: 'bold'
                      }}
                    >
                      通过并出款
                    </button>
                    <button 
                      onClick={() => handleRejectWithdraw(w.id)}
                      style={{ 
                        flex: 1, padding: '6px', fontSize: '11px', borderRadius: '6px', border: '1px solid rgba(255, 60, 60, 0.4)', 
                        background: 'rgba(255, 60, 60, 0.1)', color: 'hsl(0, 90%, 65%)', cursor: 'pointer', fontWeight: 'bold'
                      }}
                    >
                      驳回并退款
                    </button>
                  </div>
                </div>
              ))}
              {adminWithdrawals.filter(w => w.status === 'pending_approval').length === 0 && (
                <div style={{ textAlign: 'center', fontSize: '11px', color: 'var(--text-muted)', padding: '20px 0' }}>
                  🎉 暂无大额风审提现，系统一切正常。
                </div>
              )}
            </div>
          </section>

          {/* 实时交易审计流水 */}
          <section className="glass-card" style={{ margin: '0 16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <h3 style={{ fontSize: '15px', color: 'var(--color-primary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
              📊 D1 实时资金变动明细 (对账审计)
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '300px', overflowY: 'auto' }}>
              {userTransactions.map((tx: any) => {
                const isProfit = tx.type === 'win' || tx.type === 'deposit' || tx.type === 'refund';
                const sign = isProfit ? '+' : '-';
                const color = isProfit ? 'hsl(140, 90%, 60%)' : 'hsl(0, 90%, 65%)';
                return (
                  <div key={tx.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 4px', borderBottom: '1px solid var(--border-glass)', fontSize: '11px' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ 
                          padding: '2px 6px', borderRadius: '4px', fontSize: '9px', fontWeight: 'bold',
                          background: tx.type === 'bet' ? 'rgba(255,255,255,0.06)' : tx.type === 'win' ? 'rgba(0,255,128,0.1)' : 'rgba(0,190,255,0.1)',
                          color: tx.type === 'bet' ? 'var(--text-muted)' : tx.type === 'win' ? 'hsl(140, 90%, 60%)' : 'var(--color-primary)'
                        }}>
                          {tx.type === 'bet' ? '下注' : tx.type === 'win' ? '赢金' : tx.type === 'deposit' ? '充值' : tx.type === 'withdraw' ? '提现' : '退款'}
                        </span>
                        <span style={{ color: 'var(--text-main)' }}>
                          {tx.game_id ? tx.game_id.toUpperCase() : 'PLATFORM'}
                        </span>
                      </div>
                      <div style={{ fontSize: '8px', color: 'var(--text-muted)', marginTop: '4px' }}>
                        {new Date(tx.created_at).toLocaleString()}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <strong style={{ color, fontSize: '12px' }}>
                        {sign}{tx.amount} {tx.currency.toUpperCase()}
                      </strong>
                      <div style={{ fontSize: '9px', color: 'var(--text-muted)', marginTop: '2px' }}>
                        余额: {tx.balance_after ? tx.balance_after.toFixed(2) : 'N/A'}
                      </div>
                    </div>
                  </div>
                );
              })}
              {userTransactions.length === 0 && (
                <div style={{ textAlign: 'center', fontSize: '11px', color: 'var(--text-muted)', padding: '25px 0' }}>
                  暂无资金变动流水。
                </div>
              )}
            </div>
          </section>
        </div>
      )}

      {/* 5. 弹窗组件 - 充值 (Deposit) */}
      {showDepositModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.8)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="glass-card" style={{ width: '90%', maxWidth: '400px', padding: '24px' }}>
            <h4 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '12px' }}>充值代币 ({activeCurrency.toUpperCase()})</h4>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '14px' }}>
              请转账到下方平台分配的 HD 隔离充值地址。转账完成后系统将自动进行链上二次确认并上分：
            </p>
            <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-glass-active)', borderRadius: '8px', padding: '10px', fontSize: '11px', wordBreak: 'break-all', fontFamily: 'monospace', marginBottom: '16px', color: 'var(--color-primary)' }}>
              {depositAddress}
            </div>
            <button className="btn-neon" onClick={handleDepositSubmit} style={{ width: '100%', padding: '10px' }}>
              我已完成支付
            </button>
          </div>
        </div>
      )}

      {/* 6. 弹窗组件 - 提现 (Withdraw) */}
      {showWithdrawModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.8)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="glass-card" style={{ width: '90%', maxWidth: '400px', padding: '24px' }}>
            <h4 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '12px' }}>提现代币 ({activeCurrency.toUpperCase()})</h4>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' }}>
              <div>
                <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>接收钱包地址 ({CURRENCIES[activeCurrency].chain.toUpperCase()})</label>
                <input type="text" value={withdrawAddress} onChange={(e) => setWithdrawAddress(e.target.value)} placeholder="请输入您的钱包收币地址" style={{ width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-glass)', borderRadius: '6px', padding: '8px', color: 'var(--text-main)', marginTop: '4px' }} />
              </div>
              <div>
                <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>提现金额</label>
                <input type="number" value={withdrawAmount} onChange={(e) => setWithdrawAmount(e.target.value)} placeholder={`起提金额: ${CURRENCIES[activeCurrency].minWithdraw}`} style={{ width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-glass)', borderRadius: '6px', padding: '8px', color: 'var(--text-main)', marginTop: '4px' }} />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button className="btn-neon" onClick={handleWithdrawSubmit} style={{ flex: 1, padding: '10px' }}>
                确认提现
              </button>
              <button className="btn-neon" onClick={() => setShowWithdrawModal(false)} style={{ flex: 1, padding: '10px', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-glass)', boxShadow: 'none' }}>
                取消
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 7. 弹窗组件 - 公平性校验 (Provably Fair Modal) */}
      {showFairnessModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.8)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="glass-card" style={{ width: '90%', maxWidth: '420px', padding: '24px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h4 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <ShieldCheck color="var(--color-primary)" /> 可信公平算法即时验证
            </h4>
            <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '14px' }}>
              9F 采用 HMAC-SHA256 公平哈希验证。在此输入您在局后获取的明文服务端种子、客户端种子和 Nonce 值，直接在客户端比对校验游戏结果：
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' }}>
              <div>
                <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>服务端明文种子 (Plain Server Seed)</label>
                <input type="text" value={verifyServerSeed} onChange={(e) => setVerifyServerSeed(e.target.value)} style={{ width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-glass)', borderRadius: '6px', padding: '8px', color: 'var(--text-main)', marginTop: '4px', fontFamily: 'monospace', fontSize: '11px' }} />
              </div>
              <div>
                <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>客户端种子 (Client Seed)</label>
                <input type="text" value={verifyClientSeed} onChange={(e) => setVerifyClientSeed(e.target.value)} style={{ width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-glass)', borderRadius: '6px', padding: '8px', color: 'var(--text-main)', marginTop: '4px', fontFamily: 'monospace', fontSize: '11px' }} />
              </div>
              <div>
                <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>游戏局数 (Nonce)</label>
                <input type="number" value={verifyNonce} onChange={(e) => setVerifyNonce(parseInt(e.target.value))} style={{ width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-glass)', borderRadius: '6px', padding: '8px', color: 'var(--text-main)', marginTop: '4px' }} />
              </div>
            </div>

            {verifyResult && (
              <div style={{ background: '#000', borderRadius: '8px', padding: '10px', fontSize: '11px', fontFamily: 'monospace', marginBottom: '16px', color: 'var(--color-primary)', whiteSpace: 'pre-line', wordBreak: 'break-all' }}>
                {verifyResult}
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px' }}>
              <button className="btn-neon" onClick={() => handleVerifyFairness()} style={{ flex: 1, padding: '10px' }}>
                执行校验
              </button>
              <button className="btn-neon" onClick={() => { setShowFairnessModal(false); setVerifyResult(null); }} style={{ flex: 1, padding: '10px', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-glass)', boxShadow: 'none' }}>
                关闭
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 渐隐式 Toast 容器 */}
      {toast && (
        <div className="toast-container">
          <div className="toast-card">
            <span>{toast.type === 'success' ? '🎉' : toast.type === 'error' ? '❌' : 'ℹ️'}</span>
            <div style={{ whiteSpace: 'pre-line' }}>{toast.message}</div>
          </div>
        </div>
      )}
    </div>
  );
}
